/*
 * Copyright (c) 2022. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import EditIcon from '@mui/icons-material/Edit';
import WarningRounded from '@mui/icons-material/WarningRounded';
import {
  type AutocompleteProps,
  Collapse,
  Grid,
  IconButton,
  Link as MUILink,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Stack } from '@mui/system';
import type { AbsoluteURL, ComponentRecord, LooseUnary, PathKeys } from '@poweralpha/common';
import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatComponentAttribute } from '../../utils';
import InputKeyValue from '../display';
import { ArchivedTableRow } from '../tables';
import FormAutocomplete, { type CustomAutocompleteProps } from './FormAutocomplete';
import { useFieldContext } from './formContext';

function isOptionEqual(option: ComponentRecord<unknown>, value: ComponentRecord<unknown>) {
  return option.id === value.id;
}

type ComponentAutocompleteProps<
  T extends ComponentRecord<unknown>,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
> = Omit<
  Omit<AutocompleteProps<T, Multiple, DisableClearable, false>, 'size'> &
    CustomAutocompleteProps<T, Multiple, DisableClearable, false>,
  'renderInput' | 'freeSolo' | 'handleCreate'
>;

interface Props<
  T extends ComponentRecord<unknown>,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
> extends ComponentAutocompleteProps<T, Multiple, DisableClearable> {
  isLoading: boolean;
  customAttrs?: Partial<
    Record<
      LooseUnary<keyof T['data'] & string>,
      string | { label: string; scale?: number; precision?: number; url?: string }
    >
  >;
  componentLink?: AbsoluteURL;
  edit?: { disabled?: boolean; onClick: () => void; error?: boolean };
  size?: AutocompleteProps<T, Multiple, DisableClearable, false>['size'] | 'tiny' | 'xs';
  customError?: string;
  displayOnly?: boolean;
}

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: fine
function FormComponentAutocomplete<
  T extends ComponentRecord<unknown>,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
>({
  customAttrs,
  getOptionLabel,
  componentLink,
  edit,
  customError,
  ...props
}: Props<T, Multiple, DisableClearable>) {
  const field = useFieldContext<T | null>();
  const selected = field.state.value;

  const [focused, setFocused] = useState(false);

  const removeFocus = useCallback(() => setTimeout(() => setFocused(false), 3e3), []);

  return (
    <>
      {props.displayOnly ? (
        <InputKeyValue
          value={field.state.value?.id}
          label={props.label}
          linkRef={
            componentLink
              ? (id) => ({
                  name: (getOptionLabel?.(field.state.value as T) ?? field.state.value) as string,
                  path: `${componentLink}/${id}` as AbsoluteURL,
                })
              : undefined
          }
          sx={{ pt: 0, pb: 0 }}
        />
      ) : (
        <Stack direction="row" spacing={2}>
          <Grid size={12}>
            <AnimatePresence>
              <motion.div
                initial={{ x: 0, y: 0, width: '100%', opacity: 0.7 }}
                animate={{ x: 0, y: 0, width: selected?.archived ? '90%' : '100%', opacity: 1 }}
                exit={{ x: 0, y: 0, width: selected?.archived ? '90%' : '100%', opacity: 0.7 }}
                transition={{ duration: 0.2 }}
                style={{
                  position: 'relative',
                  top: 0,
                  right: 0,
                  transformOrigin: 'top right',
                  zIndex: 1200,
                  display: 'inline-block',
                }}
              >
                <FormAutocomplete
                  {...props}
                  freeSolo={false}
                  getOptionLabel={getOptionLabel}
                  isOptionEqualToValue={isOptionEqual}
                  onFocus={() => setFocused(true)}
                  onBlur={removeFocus}
                  onInputChange={removeFocus}
                />
              </motion.div>
              {selected?.archived && (
                <motion.div
                  initial={{ x: 0, y: 0, width: 28, opacity: 0.7 }}
                  animate={{ x: 0, y: 0, width: 28, opacity: 1 }}
                  exit={{ x: 0, y: 0, width: 28, opacity: 0.7 }}
                  transition={{ duration: 0.2 }}
                  style={{
                    position: 'relative',
                    top: '15px',
                    left: '10px',
                    transformOrigin: 'top right',
                    overflow: 'hidden',
                    zIndex: 1200,
                    display: 'inline-block',
                  }}
                >
                  <Tooltip title="Archived">
                    <WarningRounded color="warning" />
                  </Tooltip>
                </motion.div>
              )}
            </AnimatePresence>
          </Grid>
          {edit && (
            <Grid>
              <IconButton
                onClick={edit.onClick}
                color={edit.error ? 'error' : 'primary'}
                disabled={edit.disabled}
                data-testid={`edit-${field.name}`}
              >
                <EditIcon />
              </IconButton>
            </Grid>
          )}
        </Stack>
      )}
      {customError && (
        <Typography
          sx={{
            fontSize: 12,
            color: 'error.main',
            mt: 0.5,
            ml: 1.5,
          }}
        >
          {customError}
        </Typography>
      )}
      <Collapse in={selected ? focused : false}>
        <TableContainer>
          <Table size="small">
            <TableBody>
              {componentLink && selected && (
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell align="right">
                    <MUILink
                      component={Link}
                      to={`${componentLink}/${selected?.id}`}
                      target="_blank"
                    >
                      {getOptionLabel?.(selected) ?? selected?.id}
                    </MUILink>
                  </TableCell>
                </TableRow>
              )}
              {customAttrs &&
                selected &&
                Object.entries(customAttrs).map(([key, attr]) => (
                  <TableRow key={key}>
                    <TableCell>{typeof attr === 'string' ? attr : attr?.label}</TableCell>
                    <TableCell align="right">
                      {formatComponentAttribute(
                        selected?.data as T | undefined,
                        key as PathKeys<T>,
                        {
                          scale: typeof attr !== 'string' ? attr?.scale : undefined,
                          precision: typeof attr !== 'string' ? attr?.precision : undefined,
                        },
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              <ArchivedTableRow archived={selected?.archived} />
            </TableBody>
          </Table>
        </TableContainer>
      </Collapse>
    </>
  );
}

export default FormComponentAutocomplete;
